import { formatCurrency } from '@shared/SharedAccountBody';
import { RelativeTime } from '@shared/RelativeTime';
import { RefreshIcon } from '@shared/RefreshIcon';
import { RetailAccountContent } from '@shared/types';
import { ALPACA_LINKS } from '../../onboarding/onboardingSteps';
import { Button } from '@/components/ui/button';
import { DataCard } from '@/components/nq/DataCard';

interface PortfolioUnfundedHeroProps {
  content: RetailAccountContent;
  onRefresh: () => void;
  refreshing?: boolean;
  lastUpdatedAt?: Date | null;
}

const PortfolioUnfundedHero = ({
  content,
  onRefresh,
  refreshing = false,
  lastUpdatedAt,
}: PortfolioUnfundedHeroProps) => {
  const balance = content.totalValue ?? 0;

  return (
    <section className="mb-6">
      <div
        className="mb-4 rounded-xl border border-secondary/35 bg-[image:linear-gradient(135deg,rgba(255,193,7,0.12),rgba(255,255,255,0.03))] px-8 py-7"
        role="status"
      >
        <h2 className="mb-3 font-display text-[1.35rem] font-bold leading-snug text-foreground">
          Your account needs to be funded through Alpaca.markets
        </h2>
        <p className="mb-2.5 text-[1.05rem] text-foreground/85">
          Current balance is{' '}
          <strong className="font-bold text-[color:var(--nq-warn)]">
            {formatCurrency(balance)}
          </strong>
        </p>
        <p className="mb-5 max-w-[52ch] text-sm leading-relaxed text-muted-foreground">
          Deposit funds in your Alpaca dashboard, then return here and refresh.
          NovaQuantis will pick up your balance automatically.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button asChild variant="secondary">
            <a
              href={ALPACA_LINKS.dashboard}
              target="_blank"
              rel="noopener noreferrer"
            >
              Fund account on Alpaca
            </a>
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onRefresh}
            disabled={refreshing}
            title="Refresh account balance"
            aria-label={refreshing ? 'Refreshing…' : 'Refresh account balance'}
            className="h-auto items-center gap-2 px-4 py-2 text-left text-[13px] font-semibold normal-case"
          >
            <RefreshIcon
              spinning={refreshing}
              className={`shrink-0${refreshing ? ' animate-spin' : ''}`}
            />
            <span className="flex min-w-0 flex-col items-start gap-px">
              <span className="text-[13px] font-semibold leading-tight">
                {refreshing ? 'Refreshing…' : 'Refresh balance'}
              </span>
              {lastUpdatedAt && !refreshing && (
                <span
                  className="whitespace-nowrap text-[10px] font-medium leading-tight opacity-75"
                  aria-live="polite"
                >
                  Updated <RelativeTime value={lastUpdatedAt} />
                </span>
              )}
            </span>
          </Button>
        </div>
      </div>

      <DataCard
        className="rounded-[14px] bg-[color:var(--nq-surface)] p-5 opacity-75 shadow-[var(--shadow-card)]"
        title="Performance"
      >
        <div className="flex min-h-[280px] items-center justify-center text-sm text-muted-foreground">
          Performance tracking starts after your first deposit.
        </div>
      </DataCard>
    </section>
  );
};

export default PortfolioUnfundedHero;
